import IconButton from 'components/IconButton';
import { useAtom } from 'jotai';
import React, { useState, useEffect } from 'react';
import { ButtonGroup, UncontrolledTooltip } from 'reactstrap';

import { TimespanFilter, IntervalFilter } from '../../types';

import 'views/Admin/Dashboard/styles.css';
import { mapDurationToInterval } from './helper';

type Props = {
  timeSpanAtom: any;
  intervalAtom: any;
  showHistoryChartCountsAtom: any;
};

const HistoryChartHeader = ({
  timeSpanAtom,
  intervalAtom,
  showHistoryChartCountsAtom,
}: Props) => {
  const [timespan, setTimespan] = useAtom(timeSpanAtom);
  const [intervalFilter, setIntervalFilter] = useAtom(intervalAtom);
  const [showHistoryChartCounts, setShowHistoryChartCounts] = useAtom(
    showHistoryChartCountsAtom,
  );
  const [allowedIntervals, setAllowedIntervals] = useState<any>([]);

  useEffect(() => {
    // Update allowed intervals when the timespan changes
    updateAllowedIntervals(timespan);
  }, [timespan]);

  const updateAllowedIntervals = (selectedDuration) => {
    // Map durations to corresponding intervals
    const allowedIntervals = mapDurationToInterval(selectedDuration);
    setAllowedIntervals(allowedIntervals);
    // Reset interval if the current interval is not allowed
    if (!allowedIntervals.includes(intervalFilter)) {
      setIntervalFilter(allowedIntervals[0]);
    }
  };

  const handleTimespanChange = (selectedTimespan: TimespanFilter) => {
    setTimespan(selectedTimespan);
    updateAllowedIntervals(selectedTimespan);
  };

  const handleIntervalChange = (selectedInterval: IntervalFilter) => {
    setIntervalFilter(selectedInterval);
  };

  const TIMESPAN_OPTIONS = Object.keys(TimespanFilter).map((key) => {
    const isActive = timespan === TimespanFilter[key];
    const className = `btn c-group-btn${isActive ? ' active' : ''}`;

    return (
      <button
        key={key}
        className={className}
        onClick={() => handleTimespanChange(TimespanFilter[key])}
      >
        {TimespanFilter[key]}
      </button>
    );
  });

  const INTERVAL_OPTIONS = Object.keys(IntervalFilter).map((key) => {
    const currentInterval = IntervalFilter[key] as IntervalFilter;
    const isActive = intervalFilter === currentInterval;
    const isDisabled = !allowedIntervals.includes(currentInterval);
    const className = `btn c-group-btn${isActive ? ' active' : ''}${
      isDisabled ? ' disabled' : ''
    }`;

    return (
      <button
        key={key}
        className={className}
        onClick={() => !isDisabled && handleIntervalChange(currentInterval)}
      >
        {currentInterval}
      </button>
    );
  });

  return (
    <div className="d-sm-flex flex-sm-row float-right">
      {/* <Label>Duration</Label> */}
      <ButtonGroup size="sm" className="c-btn-group">
        <div className="input-group-prepend c-label d-flex">
          <div id="duration_label" className="input-group-text">
            Duration
          </div>
          <UncontrolledTooltip placement="top" target="duration_label">
            Specifies the time frame covered by the trending charts.
          </UncontrolledTooltip>
        </div>
        {TIMESPAN_OPTIONS}
      </ButtonGroup>
      <ButtonGroup size="sm" className="c-btn-group mx-2">
        <div className="input-group-prepend c-label">
          <div id="interval_label" className="input-group-text">
            Interval
          </div>
          <UncontrolledTooltip placement="top" target="interval_label">
            Specifies the time period represented by each data point in the
            trending charts.
          </UncontrolledTooltip>
        </div>
        {INTERVAL_OPTIONS}
      </ButtonGroup>
      <IconButton
        id="showCounts"
        iconClass={
          showHistoryChartCounts ? 'fa fa-eye-slash fa-lg' : 'fa fa-eye fa-lg'
        }
        tooltip="Toggle Counts"
        active={!!showHistoryChartCounts}
        onClick={() => {
          setShowHistoryChartCounts(!showHistoryChartCounts);
        }}
      />
    </div>
  );
};

export default HistoryChartHeader;
